import capitalize from 'lodash/capitalize';
import React, { use, useCallback, useMemo, useState } from 'react';

import HeaderClubSearch from 'src/components/layout/redesign/global-header-mobile/header-club-search';
import HeaderMobile from 'src/components/layout/redesign/header-mobile';
import PromoRibbon from 'src/components/layout/redesign/header-shared/promo-ribbon';
import RecentlyViewedClubRibbon from 'src/components/layout/redesign/header-shared/recently-viewed-club-ribbon';
import type { GlobalHeaderContent } from 'src/content/cms-provider/global-cms-provider';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { useClientSnapshot } from 'src/custom-hooks/use-client-snapshot';
import { BuildType } from 'src/enums/build/build-type';
import { RedesignButtonStyle, RedesignHarbourButtonStyle } from 'src/enums/button-style';
import { amplitudeButtonTrackEvent } from 'src/helpers/amplitude-event-track';
import { getLocalStorageItem } from 'src/helpers/local-storage-helper';
import { RecentlyViewedClubStorageKey } from 'src/helpers/recently-viewed-club-storage-keys';

const getShouldShowClubRibbon = (headerContent: GlobalHeaderContent) => {
  if (!headerContent?.headerRecentlyViewedClubRibbon?.enabled) {
    return false;
  }

  return !!getLocalStorageItem(RecentlyViewedClubStorageKey.clubId);
};

const getPrimaryButtonCTAName = (buildType: BuildType, headerContent: GlobalHeaderContent) => {
  switch (buildType) {
    case BuildType.HARBOUR:
    case BuildType.MERIDIAN:
      return 'Membership in Navigation';
    case BuildType.TOGETHER_FOUNDATION:
      return headerContent?.headerButton?.buttonText ?? 'Header Primary Button';
    default:
      return 'Join Club in Navigation';
  }
};

type GlobalHeaderMobileProps = {
  forceMobileHeader?: boolean;
  forcePromoRibbonVisible?: boolean;
};

const GlobalHeaderMobile = ({
  forceMobileHeader = false,
  forcePromoRibbonVisible = false,
}: GlobalHeaderMobileProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClubSearchOpen, setIsClubSearchOpen] = useState(false);

  const { headerContent } = use(GlobalCmsContent);
  const { pageLevel, buildType, isHarbour, language, isDL } = use(SiteContext);

  const getShouldShowClubRibbonFromStorage = useCallback(
    () => getShouldShowClubRibbon(headerContent),
    [headerContent]
  );
  const shouldShowClubRibbonFromStorage = useClientSnapshot(
    getShouldShowClubRibbonFromStorage,
    false
  );
  const [hasDismissedClubRibbon, setHasDismissedClubRibbon] = useState(false);
  const shouldShowClubRibbon = shouldShowClubRibbonFromStorage && !hasDismissedClubRibbon;

  const handleMenuOpen = useCallback((isOpen: boolean) => {
    setIsMobileMenuOpen(isOpen);

    if (!isOpen) {
      setIsClubSearchOpen(false);
    }
  }, []);

  const searchButton = useMemo(() => {
    return headerContent?.searchSettings && isDL
      ? ({
          type: 'button',
          buttonStyle: isHarbour
            ? RedesignHarbourButtonStyle.TRANSPARENT_WITH_GREY_FONT_AND_BORDER
            : RedesignButtonStyle.TRANSPARENT_WITH_PLUM_FONT,
          handleClick: () => {
            void amplitudeButtonTrackEvent({
              buildType,
              language,
              button: null,
              componentName: 'Global Header Mobile',
              pageLevel,
              ctaName: ' "Find a club" - Navigation',
            });
            setIsClubSearchOpen(true);
          },
          text: headerContent.searchSettings.openSearchButtonName,
        } as const)
      : undefined;
  }, [buildType, headerContent.searchSettings, isHarbour, language, pageLevel, isDL]);

  const secondaryButton = useMemo(() => {
    return headerContent?.headerButtonSecondary?.showButton
      ? ({
          type: 'link',
          buttonProps: headerContent.headerButtonSecondary,
          handleClick: () => {
            handleMenuOpen(false);
            void amplitudeButtonTrackEvent({
              buildType,
              language,
              button: headerContent?.headerButtonSecondary,
              componentName: 'Global Header Mobile',
              pageLevel,
              ctaName: `${capitalize(headerContent.headerButtonSecondary?.buttonText ?? 'Header Secondary Button')} in Navigation`,
            });
          },
        } as const)
      : undefined;
  }, [buildType, handleMenuOpen, headerContent.headerButtonSecondary, language, pageLevel]);

  const isTogetherFoundation = buildType === BuildType.TOGETHER_FOUNDATION;

  return (
    <HeaderMobile
      headerContent={headerContent}
      forceMobileHeader={forceMobileHeader}
      primaryButton={
        headerContent?.headerButton
          ? {
              type: 'link',
              buttonProps: headerContent.headerButton,
              handleClick: () => {
                handleMenuOpen(false);
                void amplitudeButtonTrackEvent({
                  buildType,
                  language,
                  button: headerContent?.headerButton,
                  pageLevel,
                  ctaName: getPrimaryButtonCTAName(buildType, headerContent),
                  componentName: 'Global Header Mobile',
                  ...(isTogetherFoundation ? { ctaEventName: 'Primary Donate Button - Nav' } : {}),
                });
              },
            }
          : undefined
      }
      secondaryButton={secondaryButton}
      searchButton={searchButton}
      isMenuOpen={isMobileMenuOpen}
      setMenuOpen={handleMenuOpen}
      onMenuChanged={() => {
        setIsClubSearchOpen(false);
      }}
      isAdditionalSubMenuOpen={isClubSearchOpen}
      additionalSubMenus={
        <HeaderClubSearch
          isMenuOpen={isMobileMenuOpen}
          isVisible={isClubSearchOpen}
          onFinish={() => {
            handleMenuOpen(false);
          }}
        />
      }
      ribbonChildren={
        <>
          {headerContent?.headerPromoRibbon && (
            <PromoRibbon
              isMobileNavOpen={isMobileMenuOpen}
              isCollapsed={shouldShowClubRibbon}
              promoRibbon={headerContent.headerPromoRibbon}
              isMobile
              forceVisible={forcePromoRibbonVisible}
            />
          )}
          <RecentlyViewedClubRibbon
            isMobileNavOpen={isMobileMenuOpen}
            isMobile
            enabled={shouldShowClubRibbon && !isMobileMenuOpen}
            setShouldShowClubRibbon={(value) => {
              if (!value) setHasDismissedClubRibbon(true);
            }}
          />
        </>
      }
    />
  );
};

export default GlobalHeaderMobile;
